import { Policy } from '@/auth/Policy';

/** Deciden el layout de Home, no otorga permisos. */
export const Profile = {
  ADMIN: 'admin',
  ACCOUNTING: 'accounting',
  COMMERCIAL: 'commercial',
  MANAGER: 'manager',
  RECEPTION: 'reception',
} as const;
export type Profile = (typeof Profile)[keyof typeof Profile];

/** Can see the main Schedule/"Agenda" in Home. */
export const FULL_SCHEDULE_IN_HOME: Policy = Policy.onlyProfiles([
  Profile.MANAGER,
  Profile.RECEPTION,
]);

const PROFILE_BY_GROUP_NAME: Record<string, Profile> = {
  admin: Profile.ADMIN,
  contabilidad: Profile.ACCOUNTING,
  comercial: Profile.COMMERCIAL,
  encargado: Profile.MANAGER,
  recepcion: Profile.RECEPTION,
};

const GROUP_NAME_BY_PROFILE = Object.fromEntries(
  Object.entries(PROFILE_BY_GROUP_NAME).map(([group, profile]) => [
    profile,
    group,
  ]),
) as Record<Profile, string>;

function profileLabel(profile: Profile): string {
  const name = GROUP_NAME_BY_PROFILE[profile];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** Admin opaca al resto de los perfiles; sin perfiles también es Admin. */
export function profilesLabel(profiles: readonly Profile[]): string {
  if (profiles.length === 0 || profiles.includes(Profile.ADMIN)) {
    return profileLabel(Profile.ADMIN);
  }
  return profiles.map(profileLabel).join(', ');
}

function normalizeGroupName(raw: string): string {
  const lastSegment = raw.split('/').filter(Boolean).pop() ?? '';
  return lastSegment
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

const PROFILE_ROLE_PREFIX = 'profile:';

export function profileFromRole(role: string): Profile | null {
  if (!role.startsWith(PROFILE_ROLE_PREFIX)) return null;
  const value = role.slice(PROFILE_ROLE_PREFIX.length);
  return (Object.values(Profile) as string[]).includes(value)
    ? (value as Profile)
    : null;
}

function toProfile(raw: string): Profile | null {
  return PROFILE_BY_GROUP_NAME[normalizeGroupName(raw)] ?? null;
}

export function isAdminGroup(raw: string): boolean {
  return toProfile(raw) === Profile.ADMIN;
}
